import React, { useEffect, useState } from "react";
import { Box, Button, useTheme, Stack, Tooltip, TextField, FormControl, Tab, Tabs, Chip, Typography } from "@mui/material";
import AxionControls from "../../../components/controls/AxionControls";
import dayjs from "dayjs";
import messages from "./messages";
import { tokens } from "../../../assets/theme";
import { FormattedMessage, useIntl } from "react-intl";
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import { reportProductionOrderInSystem, getProductionOrderByOrderNo } from "../../../fetchers/productionOrder";
import { getScrapForPO, reportScrap, getScrapReasonCodes } from "../../../fetchers/scrap";
import { useQuery } from "@tanstack/react-query";
import { styled } from "@mui/material/styles";
import useFeatureFlags, { isFeatureEnabled } from '../../../hooks/useFeatureFlags';
import { ShowerRounded } from "@mui/icons-material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { useMsal, useAccount } from "@azure/msal-react";
import { fetchUsers } from "../../../fetchers/user";
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';

export default function ProductionOrderReportScrap(props) {

    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { accounts } = useMsal();
    const account = useAccount(accounts[0]);
    const { formatMessage } = useIntl();
    const [companyKey, setCompanyKey] = useLocalStorage("companyKey", 1);
    const [buttonDisabled, setButtonDisabled] = React.useState(false);
    const [ongoingPO, setOngoingPO] = useState(null);

    const [scrapedQTY, setScrapedQTY] = useState(0);
    const [currentScrapedQTY, setCurrentScrapedQTY] = useState(1);
    const [addQuantityInput, setAddQuantityInput] = useState("1");
    const [totalScrapedQTY, setTotalScrapedQTY] = useState(0);

    const [scrapReasonCode, setScrapReasonCode] = useState("");
    const [scrapReasonName, setScrapReasonName] = useState("");
    const [scrapComment, setScrapComment] = useState("");
    const [calculatedPrice, setCalculatedPrice] = useState(0);
    const [sign, setSign] = useLocalStorage("oee_report_sign", "");
    const [scrapCodes, setScrapCodes] = useState([]);
    const [closers, setClosers] = useState([]);

    const machineId = props.machineId;
    //const machineInfo = props.machineInfo;

    const [action, setAction] = useState("Report"); // "Start" or "Report" or "Stop" or "Edit"

    const scrapQuery = useQuery(
        ["scrap", companyKey, props.productionOrder],
        () => getScrapForPO(props.productionOrder?.productionOrderNo, props.productionOrder?.operationNumber, companyKey, false),
        {
            select: (scrap) => [...scrap].sort(
                (firstScrap, secondScrap) => new Date(firstScrap.reportedAt) - new Date(secondScrap.reportedAt)
            ),
        }
    );

    const usersQuery = useQuery(
        ["allUsers"],
        () => fetchUsers(),
        {
            staleTime: 300000,
            keepPreviousData: true
        }
    );

    const scrapReasonCodesQuery = useQuery(
        ["scrapReasonCodes", companyKey],
        () => getScrapReasonCodes(companyKey),
        {
            keepPreviousData: true
        }
    );

    useEffect(() => {
        if (!usersQuery?.data || usersQuery.isLoading) {
            return;
        }
        setClosers([{ id: account.username, title: `${formatMessage(messages.po_report_currentuser)} (${account.username})` }, ...usersQuery.data?.map((user) => ({ id: user.username, title: `${user.name} (${user.username})` }))]);
    }, [usersQuery.data]);

    useEffect(() => {
        if (!scrapReasonCodesQuery?.data || scrapReasonCodesQuery.isLoading) {
            return;
        }
        setScrapCodes([...scrapReasonCodesQuery.data?.map((code) => ({ id: code.reasonCode, title: code.reasonDescription + (code.forceComment ? " *" : "") }))]);
    }, [scrapReasonCodesQuery.data]);

    useEffect(() => {
        if (scrapQuery.data) {
            // Handle scrap data if needed
            //sum up all scrapped data rows qty
            const totalScraped = scrapQuery.data?.reduce((sum, row) => sum + (row.qty || 0), 0);

            setScrapedQTY(totalScraped);
            setCurrentScrapedQTY(0);
            setAddQuantityInput("0");
            setTotalScrapedQTY(totalScraped);
        }
    }, [scrapQuery.data]);


    useEffect(() => {
        setAction("Report");
    }, [props.action]);

    const setShowBackdrop = (show) => {
        if (props.setShowBackdrop) {
            props.setShowBackdrop(show);
        }
    }

    const handleRefreshData = () => {
        if (props.handleRefreshData) {
            props.handleRefreshData();
        }
    }

    const handleAddQtyChange = (event) => {
        const { name, value } = event.target;

        // Only allow digits and a decimal point, negative scrap quantities aren't permitted
        if (!/^\d*\.?\d*$/.test(value)) {
            return;
        }

        // Strip a leading "0" (e.g. typing "5" while the field shows "0" becomes "5", not "05")
        const normalizedValue = value.replace(/^0+(\d)/, "$1");

        setAddQuantityInput(normalizedValue);

        let oldScrapedQty = scrapedQTY;
        if (isNaN(oldScrapedQty)) {
            oldScrapedQty = 0;
        }

        // value can be a decimal number, so we need to parse it as a float
        let parsedValue = parseFloat(normalizedValue);
        if (isNaN(parsedValue)) {
            parsedValue = 0;
        }

        let totalScraped = parsedValue + parseFloat(oldScrapedQty);
        if (isNaN(totalScraped)) {
            totalScraped = 0;
        }

        setCurrentScrapedQTY(parsedValue);
        setTotalScrapedQTY(totalScraped);

    }

    const handleAddQtyBlur = (event) => {
        const { value } = event.target;

        if (value === "" || isNaN(parseFloat(value))) {
            setAddQuantityInput("0");
        }
    }

    const handleAddQty = (qty) => {

        let current = currentScrapedQTY + qty;
        if (current < 0) {
            current = 0;
        }

        //How much is reported right now
        setCurrentScrapedQTY(current);
        setAddQuantityInput(current.toString());
        setTotalScrapedQTY(current + scrapedQTY);

    }

    const getUserNameById = (userId) => {
        let user = usersQuery.data?.find(user => user.id === userId);
        user = user?.username?.split("@")[0];
        user = user?.replace(".", " ");
        //fix upper case for first letter of each word
        user = user?.split(" ").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
        return user || "";
    }

    const formatUserName = (user) => {
        let userName = user?.split("@")[0];
        userName = userName?.replace(".", " ");
        //fix upper case for first letter of each word
        userName = userName?.split(" ").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
        return userName || "";
    }

    const buildScrapObject = () => {
        let scrapInfo = scrapReasonCodesQuery?.data?.find(code => code.reasonCode === scrapReasonCode);

        let scrap = [{
            machineId: machineId,
            qty: currentScrapedQTY,
            scrapReasonCode: scrapReasonCode || "9999",
            scrapReasonName: scrapInfo?.reasonDescription || "Unknown",
            comment: scrapComment,
            calculatedPrice: calculatedPrice || 0,
            sign: sign.trim() || account?.name || "",
            operationNumber: props.productionOrder?.operationNumber,
            operationName: props.productionOrder?.operationName,
            productionOrderNumber: props.productionOrder?.productionOrderNo,
            companyID: companyKey,
            reportNumber: props.productionOrder?.reportNumber,
            product: props.productionOrder?.itemNo,

        }];

        console.log("[Debug] Scrap object to report:", scrap);

        return scrap;
    }
    const scrapMaterial = () => {
        //Validate input
        if (isNaN(currentScrapedQTY) || currentScrapedQTY === 0) {
            pushErrorMessage(formatMessage(messages.po_report_message_invalidqty));
            setShowBackdrop(false);
            setButtonDisabled(false);
            return;
        }

        if (!scrapReasonCode) {
            pushErrorMessage(formatMessage(messages.po_report_message_invalidscrapreason));
            setShowBackdrop(false);
            setButtonDisabled(false);
            return;
        }

        let scrapInfo = scrapReasonCodesQuery?.data?.find(code => code.reasonCode === scrapReasonCode);
        if (scrapInfo?.forceComment === true && scrapComment?.trim() === "") {
            pushErrorMessage(formatMessage(messages.po_report_message_forcedcomment));
            setShowBackdrop(false);
            setButtonDisabled(false);
            return;
        }

        let scrapToReport = buildScrapObject();

        reportScrap(scrapToReport).then((result) => {
            setShowBackdrop(false);
            setButtonDisabled(false);
            props.setOpenPopup(false);

            pushNotification(formatMessage(messages.po_report_message_scrapped));

            handleRefreshData();
            if (props.invalidateMachineInfo) {
                props.invalidateMachineInfo();
            }
        }).catch((error) => {
            console.log("Could not report scrap", error);
            pushErrorMessage(formatMessage(messages.po_report_message_scrapped_error) + error?.message);
            setShowBackdrop(false);
            setButtonDisabled(false);
            props.setOpenPopup(false);
        });
    }


    const columns = [
        // {
        //     field: "productionOrderNumber",
        //     headerName: "PO No",
        //     width: 100
        // },
        {
            field: "qty",
            headerName: formatMessage(messages.po_report_table_qty),
            width: 80
        },
        // {
        //     field: "scrapReasonCode",
        //     headerName: formatMessage(messages.po_report_table_scrapreasoncode),
        //     width: 50
        // },
        {
            field: "scrapReasonName",
            headerName: formatMessage(messages.po_report_table_scrapreasoncode),
            width: 120,
            renderCell: (params) => `${params.row.scrapReasonCode + " - " + params.row.scrapReasonName}`
        },
        {
            field: "sign",
            headerName: formatMessage(messages.po_report_table_user),
            width: 120,
            valueGetter: (params) => formatUserName(params.row.sign)
        },
        {
            field: "reportedAt",
            headerName: formatMessage(messages.po_report_table_updated),
            width: 150,
            renderCell: (params) => `${params.row.reportedAt.toString().replace("T", " ").replace("Z", "").slice(0, 19)}`
        },
        {
            field: "comment",
            headerName: formatMessage(messages.po_report_table_comment),
            width: 200
        },
        {
            field: "sentToErp",
            headerName: "Integration",
            width: 150,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => {
                if (!params.row.sentToErp) {
                    console.log("[Debug] sentToErp is false for row: ", dayjs().format("YYYY-MM-DD HH:mm:ss"), params.row.reportedAt);
                    if (dayjs().diff(dayjs(params.row.reportedAt), 'minute') < 5) {
                        return (
                            <Tooltip title={formatMessage(messages.po_report_tooltip_waiting_for_integration)} slotProps={{ tooltip: { sx: { fontSize: "14px" } } }}>
                                <ScheduleOutlinedIcon />
                            </Tooltip>
                        );
                    } else {
                        if (params.row.sentToErpErrorResponse === null || params.row.sentToErpErrorResponse === "") {
                            return (
                                // Increase font size for better readability.
                                <Tooltip title={formatMessage(messages.po_report_tooltip_missing_integration_error)} slotProps={{ tooltip: { sx: { fontSize: "14px" } } }}>
                                    <ReportProblemOutlinedIcon />
                                </Tooltip>
                            );
                        } else {
                            return (
                                <Tooltip title={formatMessage(messages.po_report_tooltip_integration_error, { error: params.row.sentToErpErrorResponse })} slotProps={{ tooltip: { sx: { fontSize: "14px" } } }}>
                                    <ErrorOutlineOutlinedIcon />
                                </Tooltip>
                            );
                        }
                    }
                } else {
                    return (
                        <Tooltip title={formatMessage(messages.po_report_tooltip_sent_to_erp)} slotProps={{ tooltip: { sx: { fontSize: "14px" } } }}>
                            <CheckCircleOutlineOutlinedIcon />
                        </Tooltip>
                    );
                }
            },
        },

    ];

    if (scrapQuery === undefined || scrapQuery.data === undefined || scrapQuery.isLoading) {
        return <AxionControls.AxionBackdrop open={true} />
    };
    if (usersQuery === undefined || usersQuery.data === undefined || usersQuery.isLoading) {
        return <AxionControls.AxionBackdrop open={true} />
    };
    if (scrapReasonCodesQuery === undefined || scrapReasonCodesQuery.data === undefined || scrapReasonCodesQuery.isLoading) {
        return <AxionControls.AxionBackdrop open={true} />
    };

    // Debug logs----------------------------------------------------
    //console.log("[Debug] scrapQuery: ", scrapQuery.data);
    //console.log ("[Debug] Account: ", account.name, account.username);
    //console.log("[Debug] selected user: ", sign);

    return (
        <>
            <Box width="480px">

                {(ongoingPO !== null && ongoingPO.activeProductionOrderNumber !== undefined && ongoingPO.activeProductionOrderNumber !== null && action === "Start") &&
                    <Box display="flex" justifyContent="center" mb={2} sx={{
                        //display: "grid",
                        //gridTemplateColumns: "repeat(1, 1fr)",
                        gap: 1,
                        padding: "15px",
                        marginTop: "16px",
                        border: "1pt solid #ffffff"
                    }}>
                        <FormattedMessage {...messages.activeProductionOrderRestartMessage} /></Box>
                }

                <Stack direction="row" spacing={2} marginTop={2} sx={{ width: "100%" }}>
                    <Box width="50%">
                        <TextField
                            name="addQuantity"
                            label={formatMessage(messages.po_report_addqty)}
                            value={addQuantityInput}
                            onChange={handleAddQtyChange}
                            onBlur={handleAddQtyBlur}
                            autoComplete="off"
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Box>

                    <Box width="50%" display="flex" justifyItems="flex-end">
                        {/* <Button
                            variant="contained"
                            sx={{ marginLeft: "auto", marginTop: 0, width: "150px", height: "52px", backgroundColor: colors.axionGreen[500] }}
                            onClick={() => handleAddQty(123)}
                        > Lägg till batch (123)
                        </Button> */}
                    </Box>
                </Stack>

                <Stack direction="row" spacing="auto" marginTop={2} sx={{ width: "100%" }} display="flex">

                    <Button
                        variant="contained"
                        sx={{ marginTop: 2, marginLeft: "auto", display: "block", width: "50px", height: "50px", backgroundColor: colors.axionGreen[500] }}
                        onClick={() => handleAddQty(1)}
                    > +1
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ marginTop: 2, marginLeft: "auto", display: "block", width: "50px", height: "50px", backgroundColor: colors.axionGreen[500] }}
                        onClick={() => handleAddQty(5)}
                    > +5
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ marginTop: 2, marginLeft: "auto", display: "block", width: "50px", height: "50px", backgroundColor: colors.axionGreen[500] }}
                        onClick={() => handleAddQty(10)}
                    > +10
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ marginTop: 2, marginLeft: "auto", display: "block", width: "50px", height: "50px", backgroundColor: colors.axionGreen[500] }}
                        onClick={() => handleAddQty(20)}
                    > +20
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ marginTop: 2, marginLeft: "auto", display: "block", width: "50px", height: "50px", backgroundColor: colors.axionGreen[500] }}
                        onClick={() => handleAddQty(50)}
                    > +50
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ marginTop: 2, marginLeft: "auto", display: "block", width: "50px", height: "50px", backgroundColor: colors.axionGreen[500] }}
                        onClick={() => handleAddQty(100)}
                    > +100
                    </Button>
                </Stack>
                <Stack direction="row" spacing="auto" marginTop={2} marginBottom={1} sx={{ width: "100%" }} display="flex">

                    <Button
                        variant="contained"
                        sx={{ marginTop: 2, marginLeft: "auto", display: "block", width: "50px", height: "50px", backgroundColor: "#C45747" }}
                        onClick={() => handleAddQty(-1)}
                    > -1
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ marginTop: 2, marginLeft: "auto", display: "block", width: "50px", height: "50px", backgroundColor: "#C45747" }}
                        onClick={() => handleAddQty(-5)}
                    > -5
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ marginTop: 2, marginLeft: "auto", display: "block", width: "50px", height: "50px", backgroundColor: "#C45747" }}
                        onClick={() => handleAddQty(-10)}
                    > -10
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ marginTop: 2, marginLeft: "auto", display: "block", width: "50px", height: "50px", backgroundColor: "#C45747" }}
                        onClick={() => handleAddQty(-20)}
                    > -20
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ marginTop: 2, marginLeft: "auto", display: "block", width: "50px", height: "50px", backgroundColor: "#C45747" }}
                        onClick={() => handleAddQty(-50)}
                    > -50
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ marginTop: 2, marginLeft: "auto", display: "block", width: "50px", height: "50px", backgroundColor: "#C45747" }}
                        onClick={() => handleAddQty(-100)}
                    > -100
                    </Button>
                </Stack>



                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} marginTop={2}>

                    {/* <TextField
                        name="scrapReasonCode"
                        label={formatMessage(messages.po_report_table_scrapreasoncode)}
                        value={scrapReasonCode}
                        onChange={(event) => setScrapReasonCode(event.target.value)}
                        autoComplete="off"
                        sx={{ marginTop: 2, width: "175px" }}
                    /> */}
                    <Box>
                        <AxionControls.AxionSelect
                            label={formatMessage(messages.po_report_table_scrapreasoncode)}
                            sx={{ width: '175px' }}
                            options={scrapCodes}
                            showNoneOption={false}
                            name="scrapReasonCode"
                            value={scrapReasonCode}
                            onChange={(event) => setScrapReasonCode(event.target.value)}
                            searchField={true}
                            searchFieldPlaceholder={formatMessage(messages.po_report_searchreasoncode)}
                        />
                    </Box>

                    {/* 
                    <TextField
                        name="sign"
                        label={formatMessage(messages.po_report_sign)}
                        value={sign}
                        onChange={(event) => setSign(event.target.value)}
                        autoComplete="off"
                        fullWidth
                        sx={{ flex: 1 }}
                    /> */}
                    <Box>
                        <AxionControls.AxionSelect
                            label={formatMessage(messages.po_report_sign)}
                            sx={{ width: '290px' }}
                            options={closers}
                            showNoneOption={false}
                            name="activityCloser"
                            value={sign}
                            onChange={(event) => setSign(event.target.value)}
                            searchField={true}
                            searchFieldPlaceholder={formatMessage(messages.po_report_searchuser)}
                        />
                    </Box>
                </Stack>

                <Box paddingLeft="5px" marginTop="10px"><Typography fontSize="10px">{formatMessage(messages.po_scrap_madatorycomment)}</Typography></Box>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} marginTop={2}>
                    <TextField
                        width="280px"
                        name="scrapComment"
                        label={formatMessage(messages.po_report_comment)}
                        value={scrapComment}
                        onChange={(event) => setScrapComment(event.target.value)}
                        autoComplete="off"
                        multiline
                        minRows={3}
                        //fullWidth
                        sx={{ flex: 2 }}
                    />
                </Stack>
            </Box>

            <Stack direction="row" spacing={2} justifyContent="flex-end" alignItems="flex-end" marginTop="20px">
                <Button
                    variant="contained"
                    sx={{
                        marginTop: "20px",
                        marginLeft: "auto",
                        display: "block",
                        width: "140px",

                    }}
                    onClick={scrapMaterial}
                >
                    {/* {formatMessage(messages.updPplBtnUpdateOrder)} */}
                    {formatMessage(messages.po_report_btnreport)}
                </Button>
            </Stack>
            {/* 
            <Stack direction="row" spacing={1} sx={{ paddingTop: "30px" }}>
                <TextField
                    name="scrapedQuantity"
                    label="Scraped Qty" //{formatMessage(messages.scrappedQuantityLabel)}
                    value={scrapedQTY}
                    autoComplete="off"
                    //onChange={handleScrappedQtyChange}
                    disabled={true}
                    InputLabelProps={{
                        shrink: true
                    }}
                />
                <TextField
                    name="reportedQuantity"
                    label="Current Scraped Qty" //{formatMessage(messages.reportedQuantityLabel)}
                    value={totalScrapedQTY}
                    autoComplete="off"
                    //onChange={handleReportedQtyChange}
                    disabled={true}
                    InputLabelProps={{
                        shrink:
                            totalScrapedQTY !== null &&
                            totalScrapedQTY !== undefined &&
                            totalScrapedQTY !== "",
                    }}
                />
            </Stack> */}

            {scrapQuery.data?.length > 0 && (
                <>
                    <Box height="300px" marginTop="20px">
                        <DataGridPro
                            paddingTop="20px"
                            height="200px"
                            //autoHeight
                            key={"GridID"}
                            rows={scrapQuery.data || []}
                            columns={columns}
                            checkboxSelection={false}
                            disableSelectionOnClick={true}
                            //getRowClassName={getRowClassName}
                            sx={{

                                border: '1px solid ' + colors.primary[500],
                                '& .MuiDataGrid-cell': {
                                    fontSize: '14px',
                                    borderBottom: '1px solid ' + colors.primary[500],
                                },
                                '& .MuiDataGrid-columnHeaders': {
                                    borderBottom: '1px solid ' + colors.primary[500],
                                },
                                '& .MuiDataGrid-columnHeaderTitle': {
                                    fontSize: '16px',
                                    whiteSpace: 'normal',
                                    lineHeight: 1.2,

                                    //fontWeight: 'bold',
                                },
                                '& .active-po-row': {
                                    backgroundColor: colors.axionGreen[500],
                                },

                                '& .active-po-row:hover': {
                                    backgroundColor: colors.axionGreen[600],
                                },
                                '& .paused-po-row': {
                                    backgroundColor: "#C7A84B",
                                },
                                '& .paused-po-row:hover': {
                                    backgroundColor: "#C7A84B",
                                },
                                '& .stopped-po-row': {
                                    backgroundColor: "#C45747",
                                },
                                '& .stopped-po-row:hover': {
                                    backgroundColor: "#B85C4A",
                                },
                            }}
                        />

                    </Box>
                </>

            )}


        </>
    );
}